import { ref, get, set } from 'firebase/database';
import { database } from './firebaseConfig';

// ============================================================
// Утиліти для генерації квитанції (тільки для категорії "Переказ")
// ============================================================

// --- Детермінований PRNG за рядком-сідом (щоб значення були стабільні
//     для однієї й тієї ж транзакції/користувача при кожному рендері) ---
function hashSeed(str) {
  let h = 2166136261;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ============================================================
// Номер квитанції: 4 блоки по 4 символи через дефіс
// Напр.: 6HMB-1760-07C1-T0EP — стабільний для конкретної транзакції.
// ============================================================
const RECEIPT_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export function generateReceiptNumber(seed) {
  const rand = mulberry32(hashSeed(`receipt:${seed}`));
  let out = '';
  for (let i = 0; i < 16; i++) {
    out += RECEIPT_ALPHABET[Math.floor(rand() * RECEIPT_ALPHABET.length)];
  }
  const groups = [out.slice(0, 4), out.slice(4, 8), out.slice(8, 12), out.slice(12, 16)];
  return groups.join('-');
}

// Код авторизації — 6 цифр, стабільний для конкретної транзакції
export function generateAuthCode(seed) {
  const rand = mulberry32(hashSeed(`auth:${seed}`));
  return String(100000 + Math.floor(rand() * 900000));
}

// РНОКПП платника (ІПН) — 10 цифр, стабільний для конкретного користувача.
// Для квитанції «Платіж за IBAN» (поле «Код» у блоці Платник).
export function generateTaxId(seed) {
  const rand = mulberry32(hashSeed(`taxid:${seed}`));
  let out = '';
  for (let i = 0; i < 10; i++) out += Math.floor(rand() * 10);
  return out;
}

// Посилання на квитанцію check.monobank.ua/p/<токен> — токен 20 символів
// base62, стабільний для конкретної транзакції (напр. 11YeyWni6T2a6yzfEooP).
// Сторінка квитанції живе в окремому Firebase-проєкті (check-site)
export const CHECK_ORIGIN = (process.env.REACT_APP_CHECK_ORIGIN || 'https://check.monobank.lol').replace(/\/+$/, '');
// Як посилання показується на сторінці: без протоколу і з .ua замість .lol
export const CHECK_DISPLAY_HOST = CHECK_ORIGIN.replace(/^https?:\/\//, '').replace(/\.lol$/, '.ua');
const CHECK_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
// Генерує токен для квитанції (для відправлення на сервер)
export function generateCheckToken(seed) {
  const rand = mulberry32(hashSeed(`check:${seed}`));
  let token = '';
  for (let i = 0; i < 20; i++) {
    token += CHECK_ALPHABET[Math.floor(rand() * CHECK_ALPHABET.length)];
  }
  return token;
}

// Дані квитанції лежать у Realtime Database за токеном: receipts/<токен>.
// Їх пише застосунок, а читає окрема сторінка квитанції (check-site).
export async function saveReceipt(token, fields) {
  try {
    await set(ref(database, `receipts/${token}`), fields);
  } catch (error) {
    console.error('Error saving receipt:', error);
  }
}

export async function fetchReceipt(token) {
  const snap = await get(ref(database, `receipts/${token}`));
  return snap.exists() ? snap.val() : null;
}

// PDF-квитанція з сервера (той самий запит, що й у ReceiptPdfPreview)
const RECEIPT_API = process.env.REACT_APP_RECEIPT_API || 'https://13-62-19-251.sslip.io';
const RECEIPT_KEY = process.env.REACT_APP_RECEIPT_KEY || 'YwT3j4MFNhr-hl4ogNujIh58HsEoH53t';
export async function fetchReceiptPdf(fields) {
  const res = await fetch(`${RECEIPT_API}/api/receipt/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Receipt-Key': RECEIPT_KEY },
    body: JSON.stringify(fields),
  });
  if (!res.ok) throw new Error(`Сервер відповів ${res.status}`);
  return res.blob();
}

// ============================================================
// IBAN відправника: UA + 2 контрольні + 322001 (МФО Монобанку) + 19 цифр.
// Детермінований від chatId, щоб збігався з тим, що згенерував бот.
// ============================================================
export function generateIban(chatId) {
  const rand = mulberry32(hashSeed(`iban:${chatId}`));
  const check = String(10 + Math.floor(rand() * 90)); // 2 цифри
  let account = '00000'; // провідні нулі, як у справжніх рахунках Моно
  for (let i = 0; i < 14; i++) {
    account += Math.floor(rand() * 10);
  }
  return `UA${check}322001${account}`; // UA(2)+2+322001(6)+19 = 29 символів
}

// ============================================================
// Маска картки: перші 6 + ****** + останні 4  →  444111******3070
// ============================================================
export function maskCardNumber(cardNumber) {
  const digits = String(cardNumber || '').replace(/\D/g, '');
  if (digits.length < 10) return digits;
  return `${digits.slice(0, 6)}******${digits.slice(-4)}`;
}

// Номер картки одержувача (з назви транзакції-переказу) без пробілів
export function normalizeCardDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

// Переказ Монобанк з фото отримувача (bank='mono') — для нього іконку
// показуємо як звичайне фото (без зуму / рівно по контейнеру).
export function isMonoTransferAvatar(tx) {
  if (!tx) return false;
  return (tx.bank === 'mono' || tx.bank === 'monobank') && Boolean(tx.img);
}

// ============================================================
// Сума прописом (українською). Гривні — словами, копійки — цифрами.
// Напр.: 502.51 → "п'ятсот дві гривні 51 копійка"
// ============================================================
function pluralUA(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function threeDigitsToWords(n, feminine) {
  const ones = feminine
    ? ['', 'одна', 'дві', 'три', 'чотири', "п'ять", 'шість', 'сім', 'вісім', "дев'ять"]
    : ['', 'один', 'два', 'три', 'чотири', "п'ять", 'шість', 'сім', 'вісім', "дев'ять"];
  const teens = ['десять', 'одинадцять', 'дванадцять', 'тринадцять', 'чотирнадцять',
    "п'ятнадцять", 'шістнадцять', 'сімнадцять', 'вісімнадцять', "дев'ятнадцять"];
  const tens = ['', '', 'двадцять', 'тридцять', 'сорок', "п'ятдесят",
    'шістдесят', 'сімдесят', 'вісімдесят', "дев'яносто"];
  const hundreds = ['', 'сто', 'двісті', 'триста', 'чотириста', "п'ятсот",
    'шістсот', 'сімсот', 'вісімсот', "дев'ятсот"];

  const words = [];
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const o = n % 10;

  if (h) words.push(hundreds[h]);
  if (t > 1) {
    words.push(tens[t]);
    if (o) words.push(ones[o]);
  } else if (t === 1) {
    words.push(teens[o]);
  } else if (o) {
    words.push(ones[o]);
  }
  return words.join(' ');
}

function integerToWordsUA(intPart) {
  if (intPart === 0) return 'нуль';
  const parts = [];

  const millions = Math.floor(intPart / 1000000);
  const thousands = Math.floor((intPart % 1000000) / 1000);
  const rest = intPart % 1000;

  if (millions) {
    parts.push(threeDigitsToWords(millions, false));
    parts.push(pluralUA(millions, 'мільйон', 'мільйони', 'мільйонів'));
  }
  if (thousands) {
    parts.push(threeDigitsToWords(thousands, true)); // «тисяча» — жіночий рід
    parts.push(pluralUA(thousands, 'тисяча', 'тисячі', 'тисяч'));
  }
  if (rest) {
    parts.push(threeDigitsToWords(rest, true)); // «гривня» — жіночий рід
  }
  return parts.join(' ');
}

export function amountToWordsUA(amount) {
  const abs = Math.abs(Number(amount) || 0);
  const intPart = Math.floor(abs);
  const kop = Math.round((abs - intPart) * 100);

  const hryvniaWords = integerToWordsUA(intPart);
  const hryvniaLabel = pluralUA(intPart, 'гривня', 'гривні', 'гривень');
  const kopStr = String(kop).padStart(2, '0');
  const kopLabel = pluralUA(kop, 'копійка', 'копійки', 'копійок');

  return `${hryvniaWords} ${hryvniaLabel} ${kopStr} ${kopLabel}`;
}

// ============================================================
// Форматування дат для квитанції
// ============================================================
function pad2(n) {
  return String(n).padStart(2, '0');
}

// 25.07.2026
export function formatReceiptDate(isoStr) {
  const d = new Date(isoStr);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

// 25.07.2026 16:43
export function formatReceiptDateTime(isoStr) {
  const d = new Date(isoStr);
  return `${formatReceiptDate(isoStr)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
